import { isNil, isNotNil } from '@flowio/is';
import handleAdyenV3Action from './adyen/handleAdyenV3Action';
import getRollbarInstance from '../utilities/getRollbarInstance';
import type { DomReference, BaseSDKActionParams } from '../types';
import handleDisplayInlineWindowAction from './handleDisplayInlineWindowAction';
import handleExecuteScriptAction from './handleExecuteScriptAction';
import handleRedirectAction from './handleRedirectAction';
import handleStripeV3Action from './stripe/handleStripeV3Action';
import isInternalPaymentRequest from '../validators/isInternalPaymentRequest';
import { assertPaymentRequest, assertInternalPaymentRequest } from '../validators/assertTypes';
import handleKlarnaV1Action from './klarna/handleKlarnaV1Action';
import handleSelectPaymentOptionAction from './select_payment_option/handleSelectPaymentOptionAction';
import handleApplePayCreateSessionAction from './applepay/handleApplePayCreateSessionAction';

function assertMountContainerRequired(
  mountContainer: DomReference | undefined,
  actionType: io.flow.v0.unions.Action['type'],
): DomReference {
  if (isNil(mountContainer)) {
    throw new Error(`'mountContainer' argument is required for action type [${actionType}].`);
  }
  return mountContainer;
}

type HandleEitherSdkActionParams = {
  pr: io.flow.v0.models.PaymentRequest | io.flow.internal.v0.models.InternalPaymentRequest,
} & BaseSDKActionParams;

function handleEitherSdkAction({
  pr,
  mountContainer,
  buttons,
}: HandleEitherSdkActionParams): Promise<
  io.flow.v0.models.PaymentRequest
  | io.flow.internal.v0.models.InternalPaymentRequest
  | undefined
  // undefined should only be returned in the case of a redirect where the context is lost.
  > {
  /**
   * @name nextActionHandled
   * @description boolean to track if an Action has been processed this iteration.
   */
  let nextActionHandled = true;
  /**
   * @name redirectHandled
   * @description boolean to track if a redirect Action has been processed this iteration.
   */
  let redirectHandled = false;

  const action = isInternalPaymentRequest(pr) ? pr.actual_action : pr.next_action;

  return Promise.resolve().then(() => {
    if (isNotNil(action)) {
      const actionType = action.type;
      switch (actionType) {
        case 'use_sdk_applepay_js_create_session':
          return handleApplePayCreateSessionAction({
            pr,
            action,
            container: assertMountContainerRequired(mountContainer, actionType),
            buttons,
          });
        case 'use_sdk_applepay_js_complete_validation':
          /** handled internally by `handleApplePayCreateSessionAction()` */
          break;
        case 'use_sdk_klarna_v1':
          return handleKlarnaV1Action({
            pr,
            action,
            container: assertMountContainerRequired(mountContainer, actionType),
            buttons,
          });
        case 'use_sdk_adyen_v3': {
          return handleAdyenV3Action(
            pr,
            assertMountContainerRequired(mountContainer, actionType),
          );
        }
        case 'select_payment_option':
          return handleSelectPaymentOptionAction({
            pr,
            action,
            container: assertMountContainerRequired(mountContainer, actionType),
            buttons,
          });
        case 'use_sdk_stripe_v3':
          return handleStripeV3Action(
            pr,
            action,
          );
        case 'display_inline_window':
          return handleDisplayInlineWindowAction(
            pr,
            action,
            assertMountContainerRequired(mountContainer, actionType),
          );
        case 'execute_script':
          return handleExecuteScriptAction(
            pr,
            action,
            assertMountContainerRequired(mountContainer, actionType),
          );
        case 'redirect':
          handleRedirectAction(mountContainer, action);
          redirectHandled = true;
          break;
        default:
          nextActionHandled = false;
          getRollbarInstance().warn(`SDK: unhandlable next_action [${actionType}]`);
          return pr;
      }
    }
    return pr;
  }).then((nextPr) => {
    if (isNotNil(nextPr) && nextActionHandled && !redirectHandled) {
      const nextAction = isInternalPaymentRequest(nextPr)
        ? nextPr.actual_action : nextPr.next_action;
      if (isNotNil(nextAction)) {
        return handleEitherSdkAction({
          pr: nextPr,
          mountContainer,
          buttons,
        });
      }
    }
    return (!redirectHandled) ? nextPr : undefined;
  }).catch((e) => {
    getRollbarInstance().warn(`SDK: error handling next_action [${action?.type}]`, e);
    if (e instanceof Error) {
      getRollbarInstance().error(e);
      throw e;
    }
    return pr;
  });
}

export type HandleSDKActionParams = {
  pr: io.flow.v0.models.PaymentRequest,
  onComplete?: (result: io.flow.v0.models.PaymentRequest | undefined) => void,
  onFailure?: (error?: Error) => void,
} & BaseSDKActionParams;

export function handleSdkAction(
  params: HandleSDKActionParams,
): Promise<io.flow.v0.models.PaymentRequest | undefined> {
  return handleEitherSdkAction(params)
    .then(assertPaymentRequest)
    .then((pr) => {
      if (isNotNil(params.onComplete)) {
        params.onComplete(pr);
      }
      return pr;
    })
    .catch((e) => {
      const err = e instanceof Error ? e : new Error(e);
      if (isNotNil(params.onFailure)) {
        params.onFailure(err);
      }
      throw err;
    });
}

export type HandleSDKActualActionParams = {
  pr: io.flow.internal.v0.models.InternalPaymentRequest,
} & BaseSDKActionParams;

export function handleSdkActualAction(params: HandleSDKActualActionParams):
Promise<io.flow.internal.v0.models.InternalPaymentRequest | undefined> {
  return handleEitherSdkAction(params).then(assertInternalPaymentRequest);
}
